import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Calendar } from '../components/ui/calendar';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '../components/ui/popover';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { 
    BarChart3, 
    TrendingUp, 
    CheckCircle, 
    Clock,
    DollarSign,
    FileText,
    ArrowUpRight,
    ArrowDownRight,
    CalendarIcon,
    Filter,
    X,
    UserCircle,
    Edit3,
    Shield,
    Save,
    RotateCcw, Sparkles } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { SingleDayFilter, QuickRangeButtons } from '../components/dashboard/FilterComponents';

const API = `/api`;

export default function Totals() {
    const { user, getAuthHeaders } = useAuth();
    const [stats, setStats] = useState({
        total_confirmed: 0,
        total_pending: 0,
        count_confirmed: 0,
        count_pending: 0
    });
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Admin totals
    const [adminStats, setAdminStats] = useState({
        admin_total: 0,
        admin_count: 0
    });
    const [inputValue, setInputValue] = useState('');
    const [noteValue, setNoteValue] = useState('');
    const [accumulatedAdmin, setAccumulatedAdmin] = useState(0);
    const [isEditingTotal, setIsEditingTotal] = useState(false);
    
    // Filter states
    const [dateFrom, setDateFrom] = useState(null);
    const [dateTo, setDateTo] = useState(null);
    const [selectedUserId, setSelectedUserId] = useState('all');
    const [isFiltered, setIsFiltered] = useState(false);

    useEffect(() => {
        fetchInitialData();
    }, []);

    const fetchInitialData = async () => {
        try {
            const requests = [
                axios.get(`${API}/invoices/totals`, { headers: getAuthHeaders() })
            ];
            
            if (user?.role === 'admin' || user?.role === 'supervisor') {
                requests.push(axios.get(`${API}/users`, { headers: getAuthHeaders() }));
                requests.push(axios.get(`${API}/invoices/admin-totals`, { headers: getAuthHeaders() }));
            }
            
            const responses = await Promise.all(requests);
            setStats(responses[0].data);
            
            if (responses[1]) {
                setUsers(responses[1].data.filter(u => u.role === 'usuario'));
            }
            
            if (responses[2]) {
                setAdminStats(responses[2].data);
                // Load saved accumulated total
                const savedTotal = responses[2].data.adjusted_total;
                setAccumulatedAdmin(savedTotal !== null && savedTotal !== undefined ? savedTotal : 0);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const addToAccumulated = async () => {
        try {
            const valueToAdd = parseFloat(inputValue) || 0;
            const newTotal = accumulatedAdmin + valueToAdd;
            
            await axios.put(
                `${API}/invoices/admin-totals/adjusted`,
                { adjusted_total: newTotal, note: noteValue },
                { headers: getAuthHeaders() }
            );
            
            setAccumulatedAdmin(newTotal);
            setInputValue('');
            setNoteValue('');
            setIsEditingTotal(false);
        } catch (error) {
            console.error('Error saving adjustment:', error);
        }
    };

    const resetAccumulated = async () => {
        if (!window.confirm('¿Estás seguro? Esto reiniciará el total del administrador a 0.')) {
            return;
        }
        
        try {
            await axios.put(
                `${API}/invoices/admin-totals/adjusted`,
                { adjusted_total: 0, note: 'Reinicio de total' },
                { headers: getAuthHeaders() }
            );
            
            setAccumulatedAdmin(0);
            setInputValue('');
            setNoteValue('');
            setIsEditingTotal(false);
        } catch (error) {
            console.error('Error resetting:', error);
        }
    };

    // Calculate admin adjustment value (solo valores positivos)
    const adjustmentValue = accumulatedAdmin;

    const fetchFilteredTotals = async (overrides = {}) => {
        setLoading(true);
        try {
            const from = overrides.dateFrom ?? dateFrom;
            const to = overrides.dateTo ?? dateTo;
            const params = new URLSearchParams();
            if (from) params.append('date_from', format(from, 'yyyy-MM-dd'));
            if (to) params.append('date_to', format(to, 'yyyy-MM-dd'));
            if (selectedUserId && selectedUserId !== 'all') params.append('user_id', selectedUserId);
            
            const queryString = params.toString();
            
            const requests = [
                axios.get(`${API}/invoices/totals${queryString ? '?' + queryString : ''}`, { headers: getAuthHeaders() })
            ];
            
            // Also fetch admin totals with date filters
            if (user?.role === 'admin' || user?.role === 'supervisor') {
                const adminParams = new URLSearchParams();
                if (from) adminParams.append('date_from', format(from, 'yyyy-MM-dd'));
                if (to) adminParams.append('date_to', format(to, 'yyyy-MM-dd'));
                const adminQueryString = adminParams.toString();
                requests.push(axios.get(`${API}/invoices/admin-totals${adminQueryString ? '?' + adminQueryString : ''}`, { headers: getAuthHeaders() }));
            }
            
            const responses = await Promise.all(requests);
            setStats(responses[0].data);
            setIsFiltered(queryString.length > 0);
            
            if (responses[1]) {
                setAdminStats(responses[1].data);
            }
        } catch (error) {
            console.error('Error fetching filtered totals:', error);
        } finally {
            setLoading(false);
        }
    };

    const clearFilters = () => {
        setDateFrom(null);
        setDateTo(null);
        setSelectedUserId('all');
        setIsFiltered(false);
        fetchInitialData();
    };

    const selectRange = (from, to) => {
        setDateFrom(from);
        setDateTo(to);
        fetchFilteredTotals({ dateFrom: from, dateTo: to });
    };

    const hasActiveFilters = dateFrom || dateTo || (selectedUserId && selectedUserId !== 'all');

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-ES', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2
        }).format(amount);
    };

    const totalAmount = stats.total_confirmed + stats.total_pending;
    const confirmedPercentage = totalAmount > 0 
        ? ((stats.total_confirmed / totalAmount) * 100).toFixed(1) 
        : 0;

    // Calculate combined total (Total Neto + Ajuste del admin)
    const totalNeto = totalAmount * 0.97;
    const combinedTotal = totalNeto + adjustmentValue;

    const getSelectedUserName = () => {
        if (selectedUserId === 'all') return null;
        const selectedUser = users.find(u => u.id === selectedUserId);
        return selectedUser?.name || null;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="spinner"></div>
            </div>
        );
    }

    return (
        <div className="space-y-8 animate-fade-in" data-testid="totals-page">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">
                    Resumen de Totales
                </h1>
                <p className="text-slate-500 mt-1">
                    Vista general de los montos del sistema
                </p>
            </div>

            {/* Filters Section */}
            <Card className="border-0 shadow-sm">
                <CardContent className="p-4">
                    <div className="flex flex-wrap items-center gap-4">
                        {/* Date From */}
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button
                                    variant="outline"
                                    className={`w-[160px] justify-start text-left font-normal ${!dateFrom && 'text-slate-400'}`}
                                    data-testid="totals-date-from-btn"
                                >
                                    <CalendarIcon className="mr-2 h-4 w-4" />
                                    {dateFrom ? format(dateFrom, 'dd/MM/yyyy') : 'Desde'}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                                <Calendar
                                    mode="single"
                                    selected={dateFrom}
                                    onSelect={setDateFrom}
                                    locale={es}
                                    initialFocus
                                />
                            </PopoverContent>
                        </Popover>

                        {/* Date To */}
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button
                                    variant="outline"
                                    className={`w-[160px] justify-start text-left font-normal ${!dateTo && 'text-slate-400'}`}
                                    data-testid="totals-date-to-btn"
                                >
                                    <CalendarIcon className="mr-2 h-4 w-4" />
                                    {dateTo ? format(dateTo, 'dd/MM/yyyy') : 'Hasta'}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                                <Calendar
                                    mode="single"
                                    selected={dateTo}
                                    onSelect={setDateTo}
                                    locale={es}
                                    initialFocus
                                />
                            </PopoverContent>
                        </Popover>

                        <SingleDayFilter
                            day={dateFrom && dateTo && dateFrom.getTime() === dateTo.getTime() ? dateFrom : null}
                            onSelectDay={(d) => selectRange(d, d)}
                            testId="totals-single-day-btn"
                        />
                        <QuickRangeButtons
                            onSelectRange={selectRange}
                            activeFrom={dateFrom}
                            activeTo={dateTo}
                            testIdPrefix="totals"
                        />

                        {/* User Filter */}
                        <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                            <SelectTrigger className="w-[200px]" data-testid="totals-user-filter">
                                <UserCircle className="mr-2 h-4 w-4 text-slate-400" />
                                <SelectValue placeholder="Filtrar por usuario" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todos los usuarios</SelectItem>
                                {users.map((u) => (
                                    <SelectItem key={u.id} value={u.id}>
                                        {u.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>

                        {/* Filter Actions */}
                        <Button
                            onClick={() => fetchFilteredTotals()}
                            className="bg-sky-500 hover:bg-sky-600"
                            data-testid="totals-apply-filters-btn"
                        >
                            <Filter className="w-4 h-4 mr-2" />
                            Filtrar
                        </Button>

                        {hasActiveFilters && (
                            <Button
                                variant="ghost"
                                onClick={clearFilters}
                                className="text-slate-500 hover:text-slate-700"
                                data-testid="totals-clear-filters-btn"
                            >
                                <X className="w-4 h-4 mr-1" />
                                Limpiar
                            </Button>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Filter indicator */}
            {isFiltered && (
                <div className="bg-sky-50 border border-sky-200 rounded-lg p-4">
                    <p className="text-sky-800 font-medium flex items-center gap-2">
                        <Filter className="w-4 h-4" />
                        Mostrando totales filtrados
                        {getSelectedUserName() && (
                            <span className="badge badge-admin">{getSelectedUserName()}</span>
                        )}
                        {dateFrom && (
                            <span className="text-sm text-sky-600">
                                desde {format(dateFrom, 'dd/MM/yyyy')}
                            </span>
                        )}
                        {dateTo && (
                            <span className="text-sm text-sky-600">
                                hasta {format(dateTo, 'dd/MM/yyyy')}
                            </span>
                        )}
                    </p>
                </div>
            )}

            {/* Main Total Card */}
            <Card className="border-0 shadow-lg bg-gradient-to-br from-sky-500 to-sky-600 text-white">
                <CardContent className="p-8">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sky-100 text-sm font-medium uppercase tracking-wide">
                                {isFiltered ? 'Total Filtrado' : 'Total General'}
                            </p>
                            <p className="text-4xl font-bold mt-2" data-testid="total-general">
                                {formatCurrency(totalAmount)}
                            </p>
                            <p className="text-sky-100 mt-2">
                                {stats.count_confirmed + stats.count_pending} facturas {isFiltered ? 'encontradas' : 'registradas'}
                            </p>
                        </div>
                        <div className="w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                            <DollarSign className="w-10 h-10 text-white" />
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Total menos 3% Card */}
            <Card className="border-0 shadow-lg bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
                <CardContent className="p-8">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-emerald-100 text-sm font-medium uppercase tracking-wide">
                                Total Neto (-3%)
                            </p>
                            <p className="text-4xl font-bold mt-2" data-testid="total-neto">
                                {formatCurrency(totalAmount * 0.97)}
                            </p>
                            <div className="flex items-center gap-4 mt-2">
                                <p className="text-emerald-100">
                                    Descuento: {formatCurrency(totalAmount * 0.03)}
                                </p>
                                <span className="bg-white/20 px-2 py-1 rounded text-sm font-medium">
                                    -3%
                                </span>
                            </div>
                        </div>
                        <div className="w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                            <TrendingUp className="w-10 h-10 text-white" />
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Admin Invoices Card - Solo visible para admin y supervisor */}
            <Card className="border-0 shadow-lg bg-gradient-to-br from-purple-500 to-purple-600 text-white">
                <CardContent className="p-8">
                    <div className="flex items-center justify-between">
                        <div className="flex-1">
                            <div className="flex items-center gap-2">
                                <p className="text-purple-100 text-sm font-medium uppercase tracking-wide">
                                    Total del Administrador
                                </p>
                            </div>
                            
                            {/* Total acumulado */}
                            <p className="text-4xl font-bold text-white mt-2">
                                {formatCurrency(accumulatedAdmin)}
                            </p>
                            
                            {/* Campo para sumar/restar */}
                            <div className="mt-4">
                                {isEditingTotal && user?.role === 'admin' ? (
                                    <div className="bg-white/10 rounded-xl p-4">
                                        <p className="text-purple-100 text-sm mb-2">Ingresa valor a sumar (+) o restar (-):</p>
                                        <div className="flex items-center gap-3 flex-wrap">
                                            <div className="relative">
                                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-purple-800 font-bold">$</span>
                                                <Input
                                                    type="text"
                                                    inputMode="decimal"
                                                    value={inputValue}
                                                    onChange={(e) => {
                                                        const val = e.target.value;
                                                        if (val === '' || val === '-' || /^-?\d*\.?\d*$/.test(val)) {
                                                            setInputValue(val);
                                                        }
                                                    }}
                                                    className="pl-8 w-44 h-12 text-xl font-bold text-purple-800 bg-white border-0"
                                                    data-testid="admin-input-value"
                                                    placeholder="ej: 5000 o -2000"
                                                />
                                            </div>
                                            <Input
                                                type="text"
                                                value={noteValue}
                                                onChange={(e) => setNoteValue(e.target.value)}
                                                className="w-64 h-12 text-purple-800 bg-white border-0"
                                                data-testid="admin-input-note"
                                                placeholder="Nota (opcional)"
                                            />
                                        </div>
                                        <div className="flex items-center gap-3 mt-3">
                                            <Button
                                                onClick={addToAccumulated}
                                                className="bg-green-500 hover:bg-green-600 text-white"
                                                data-testid="add-admin-value-btn"
                                            >
                                                <Save className="w-4 h-4 mr-1" />
                                                Aplicar
                                            </Button>
                                            <Button
                                                onClick={() => {
                                                    setInputValue('');
                                                    setNoteValue('');
                                                    setIsEditingTotal(false);
                                                }}
                                                variant="outline"
                                                className="bg-white/20 hover:bg-white/30 text-white border-white/30"
                                            >
                                                Cancelar
                                            </Button>
                                        </div>
                                        <p className="text-purple-200 text-xs mt-2">
                                            Nuevo total será: {formatCurrency(accumulatedAdmin + (parseFloat(inputValue) || 0))}
                                        </p>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-3">
                                        {user?.role === 'admin' && (
                                            <>
                                                <Button
                                                    onClick={() => setIsEditingTotal(true)}
                                                    className="bg-white/20 hover:bg-white/30 text-white"
                                                    data-testid="edit-admin-total-btn"
                                                >
                                                    <Edit3 className="w-4 h-4 mr-1" />
                                                    Sumar/Restar
                                                </Button>
                                                <Button
                                                    onClick={resetAccumulated}
                                                    variant="outline"
                                                    className="bg-red-500/20 hover:bg-red-500/30 text-white border-red-300/50"
                                                    data-testid="reset-admin-total-btn"
                                                >
                                                    <RotateCcw className="w-4 h-4 mr-1" />
                                                    Reiniciar
                                                </Button>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                            <Shield className="w-10 h-10 text-white" />
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Total Combinado Card */}
            <Card className="border-0 shadow-lg bg-gradient-to-br from-amber-500 to-orange-500 text-white">
                <CardContent className="p-8">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-amber-100 text-sm font-medium uppercase tracking-wide">
                                Total Combinado
                            </p>
                            <p className="text-4xl font-bold mt-2" data-testid="total-combinado">
                                {formatCurrency(combinedTotal)}
                            </p>
                            <div className="flex flex-col gap-1 mt-2 text-amber-100 text-sm">
                                <span>Neto (-3%): {formatCurrency(totalNeto)}</span>
                                <span>+ Admin: {formatCurrency(adjustmentValue)}</span>
                            </div>
                        </div>
                        <div className="w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                            <DollarSign className="w-10 h-10 text-white" />
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Extra users — always separate from the regular totals */}
            {(user?.role === 'admin' || user?.role === 'supervisor') && (stats.extra_count_confirmed > 0 || stats.extra_count_pending > 0) && (
                <Card className="border-0 shadow-sm border-l-4 border-l-fuchsia-500 bg-gradient-to-r from-fuchsia-50 to-white" data-testid="extra-totals-card">
                    <CardContent className="p-5 flex flex-wrap items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-fuchsia-100 flex items-center justify-center">
                                <Sparkles className="w-5 h-5 text-fuchsia-600" />
                            </div>
                            <div>
                                <p className="text-sm text-fuchsia-800 font-semibold uppercase tracking-wide">Usuarios extra (aparte)</p>
                                <p className="text-xs text-slate-500">No suman en los totales de arriba · ver Contabilidad Extra</p>
                            </div>
                        </div>
                        <div className="flex gap-6">
                            <div>
                                <p className="text-xs text-slate-500">Confirmado</p>
                                <p className="text-xl font-bold text-fuchsia-700" data-testid="extra-total-confirmed">{formatCurrency(stats.extra_total_confirmed || 0)}</p>
                                <p className="text-xs text-slate-400">{stats.extra_count_confirmed || 0} facturas</p>
                            </div>
                            <div>
                                <p className="text-xs text-slate-500">Pendiente</p>
                                <p className="text-xl font-bold text-amber-600" data-testid="extra-total-pending">{formatCurrency(stats.extra_total_pending || 0)}</p>
                                <p className="text-xs text-slate-400">{stats.extra_count_pending || 0} facturas</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Confirmed Card */}
                <Card className="border-0 shadow-sm card-hover">
                    <CardContent className="p-6">
                        <div className="flex items-start justify-between">
                            <div className="flex-1">
                                <div className="flex items-center gap-2 mb-4">
                                    <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                                        <CheckCircle className="w-5 h-5 text-green-600" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-slate-500 uppercase tracking-wide font-medium">
                                            Confirmado
                                        </p>
                                    </div>
                                </div>
                                <p className="text-3xl font-bold text-green-600" data-testid="total-confirmed">
                                    {formatCurrency(stats.total_confirmed)}
                                </p>
                                <div className="flex items-center gap-2 mt-3">
                                    <span className="badge badge-success">
                                        {stats.count_confirmed} facturas
                                    </span>
                                    <span className="flex items-center text-green-600 text-sm font-medium">
                                        <ArrowUpRight className="w-4 h-4" />
                                        {confirmedPercentage}%
                                    </span>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Pending Card */}
                <Card className="border-0 shadow-sm card-hover">
                    <CardContent className="p-6">
                        <div className="flex items-start justify-between">
                            <div className="flex-1">
                                <div className="flex items-center gap-2 mb-4">
                                    <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                                        <Clock className="w-5 h-5 text-amber-600" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-slate-500 uppercase tracking-wide font-medium">
                                            Pendiente
                                        </p>
                                    </div>
                                </div>
                                <p className="text-3xl font-bold text-amber-600" data-testid="total-pending">
                                    {formatCurrency(stats.total_pending)}
                                </p>
                                <div className="flex items-center gap-2 mt-3">
                                    <span className="badge badge-pending">
                                        {stats.count_pending} facturas
                                    </span>
                                    <span className="flex items-center text-amber-600 text-sm font-medium">
                                        <ArrowDownRight className="w-4 h-4" />
                                        {(100 - parseFloat(confirmedPercentage)).toFixed(1)}%
                                    </span>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Progress Section */}
            <Card className="border-0 shadow-sm">
                <CardHeader>
                    <CardTitle className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-sky-500" />
                        Progreso de Confirmación
                    </CardTitle>
                    <CardDescription>
                        Porcentaje de facturas confirmadas respecto al total
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="space-y-4">
                        {/* Progress Bar */}
                        <div className="relative h-6 bg-slate-100 rounded-full overflow-hidden">
                            <div 
                                className="absolute left-0 top-0 h-full bg-gradient-to-r from-green-400 to-green-500 rounded-full transition-all duration-500"
                                style={{ width: `${confirmedPercentage}%` }}
                            />
                            <div className="absolute inset-0 flex items-center justify-center text-sm font-semibold">
                                <span className={parseFloat(confirmedPercentage) > 50 ? 'text-white' : 'text-slate-600'}>
                                    {confirmedPercentage}% Confirmado
                                </span>
                            </div>
                        </div>

                        {/* Legend */}
                        <div className="flex justify-between text-sm">
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                                <span className="text-slate-600">
                                    Confirmado: {formatCurrency(stats.total_confirmed)}
                                </span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full bg-slate-200"></div>
                                <span className="text-slate-600">
                                    Pendiente: {formatCurrency(stats.total_pending)}
                                </span>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                    { label: 'Total Facturas', value: stats.count_confirmed + stats.count_pending, icon: FileText, color: 'sky' },
                    { label: 'Confirmadas', value: stats.count_confirmed, icon: CheckCircle, color: 'green' },
                    { label: 'Pendientes', value: stats.count_pending, icon: Clock, color: 'amber' },
                    { label: '% Completado', value: `${confirmedPercentage}%`, icon: TrendingUp, color: 'purple' }
                ].map((item) => (
                    <Card key={item.label} className="border-0 shadow-sm">
                        <CardContent className="p-4 text-center">
                            <div className={`w-10 h-10 mx-auto mb-3 rounded-xl bg-${item.color}-50 flex items-center justify-center`}>
                                <item.icon className={`w-5 h-5 text-${item.color}-500`} />
                            </div>
                            <p className="text-2xl font-bold text-slate-800">{item.value}</p>
                            <p className="text-xs text-slate-500 uppercase tracking-wide mt-1">
                                {item.label}
                            </p>
                        </CardContent>
                    </Card>
                ))}
            </div>
        </div>
    );
}
